import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Shield, Lock, ArrowRight } from 'lucide-react';
import { hasRole, useXyqarAuth } from '@xyqar/shared-auth';
import GlobalFooter from './GlobalFooter';

const ENGAGE_URL      = import.meta.env.VITE_ENGAGE_URL      ?? 'https://engage.xyqar.com';
const CLIENT_URL      = import.meta.env.VITE_CLIENT_URL      ?? 'https://clients.xyqar.com';
const PARTNERS_URL    = import.meta.env.VITE_PARTNERS_URL    ?? 'https://partners.xyqar.com';
const EXPERT_URL      = import.meta.env.VITE_EXPERT_URL      ?? 'https://talent.xyqar.com';
const ADMIN_URL       = import.meta.env.VITE_ADMIN_URL       ?? 'https://admin.xyqar.com';
const AUDITOR_URL     = import.meta.env.VITE_AUDITOR_URL     ?? 'https://auditor.xyqar.com';
const EXEC_URL        = import.meta.env.VITE_EXEC_URL        ?? 'https://room.xyqar.com';
const ONBOARDING_URL  = import.meta.env.VITE_ONBOARDING_URL  ?? 'https://onboarding.xyqar.com';
const API_BASE        = import.meta.env.VITE_API_BASE        ?? 'https://api.xyqar.com';
const logoHeader      = '/logo.png';
const APPLY_INTENT_KEY = 'xyqar.genesis.apply_intent';

type ApplyIntent = {
  return_to: string;
  candidate_profile: string;
  candidate_source: string;
  candidate_tag: string;
  apply_widget_id: string;
};

function applyWidgetIdFromReturnTo(returnTo: string): string | null {
  try {
    const url = new URL(returnTo);
    if (url.pathname === '/api/v1/client/public/spontaneous-recruitment/apply') {
      return '00000000-0000-4000-8000-000000000001';
    }
    const match = url.pathname.match(/^\/api\/v1\/client\/apply-widgets\/([^/]+)\/concierge$/);
    return match?.[1] ?? null;
  } catch {
    return null;
  }
}

function isAllowedApplyReturnTo(returnTo: string): boolean {
  try {
    const url = new URL(returnTo);
    const allowedHosts = new Set(['api.xyqar.com', 'app-xyqar-api.azurewebsites.net', 'localhost', '127.0.0.1']);
    const local = url.hostname === 'localhost' || url.hostname === '127.0.0.1';
    return allowedHosts.has(url.hostname) && (url.protocol === 'https:' || (local && url.protocol === 'http:')) && !!applyWidgetIdFromReturnTo(returnTo);
  } catch {
    return false;
  }
}

function applyIntentFromLocation(): ApplyIntent | null {
  const params = new URLSearchParams(window.location.search);
  const returnTo = params.get('return_to') ?? '';
  if (!returnTo || !isAllowedApplyReturnTo(returnTo)) return null;
  const widgetId = params.get('apply_widget_id') || applyWidgetIdFromReturnTo(returnTo);
  if (!widgetId) return null;
  return {
    return_to: returnTo,
    candidate_profile: params.get('candidate_profile') || 'candidate',
    candidate_source: params.get('candidate_source') || 'client_job',
    candidate_tag: params.get('candidate_tag') || 'candidate.client.job_apply',
    apply_widget_id: widgetId,
  };
}

function readStoredApplyIntent(): ApplyIntent | null {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(APPLY_INTENT_KEY) || 'null') as ApplyIntent | null;
    return parsed && isAllowedApplyReturnTo(parsed.return_to) ? parsed : null;
  } catch {
    return null;
  }
}

function persistApplyIntent(intent: ApplyIntent | null) {
  if (intent) sessionStorage.setItem(APPLY_INTENT_KEY, JSON.stringify(intent));
}

const DotGrid: React.FC = () => (
  <div
    className="fixed inset-0 z-0 pointer-events-none"
    style={{
      backgroundImage: `radial-gradient(circle, rgba(99,102,241,0.08) 1px, transparent 1px)`,
      backgroundSize: '40px 40px',
    }}
  />
);

const GlowOrb: React.FC = () => (
  <motion.div
    className="fixed z-0 pointer-events-none rounded-full"
    style={{
      width: 600,
      height: 600,
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      background: 'radial-gradient(circle, rgba(99,102,241,0.06) 0%, transparent 70%)',
      filter: 'blur(40px)',
    }}
    animate={{ scale: [1, 1.05, 1], opacity: [0.6, 1, 0.6] }}
    transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
  />
);

export default function AuthPage() {
  const { principal, loading, login, getToken } = useXyqarAuth();
  const [step, setStep] = useState<'idle' | 'connecting'>('idle');
  const [applyError, setApplyError] = useState<string | null>(null);
  const handoffStarted = React.useRef(false);

  useEffect(() => {
    persistApplyIntent(applyIntentFromLocation());
  }, []);

  const resolvePortalUrl = React.useCallback(() => {
    if (!principal) return ENGAGE_URL;
    if (hasRole(principal, 'super_admin', 'admin'))                                          return ADMIN_URL;
    if (hasRole(principal, 'finance', 'exec'))                                               return EXEC_URL;
    if (hasRole(principal, 'auditor'))                                                       return AUDITOR_URL;
    if (hasRole(principal, 'partner'))                                                       return PARTNERS_URL;
    if (hasRole(principal, 'advisor'))                                                       return `${ONBOARDING_URL}/advisor`;
    if (hasRole(principal, 'ambassador'))                                                    return `${ONBOARDING_URL}/ambassador`;
    if (hasRole(principal, 'expert'))                                                        return EXPERT_URL;
    if (hasRole(principal, 'client_admin', 'client_manager', 'client_viewer', 'client'))    return CLIENT_URL;
    return ENGAGE_URL;
  }, [principal]);

  useEffect(() => {
    if (!loading && principal) {
      const applyIntent = applyIntentFromLocation() || readStoredApplyIntent();
      if (applyIntent) {
        if (handoffStarted.current) return;
        handoffStarted.current = true;
        setStep('connecting');
        getToken()
          .then((token) => fetch(`${API_BASE}/api/v1/client/apply-auth/handoff`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(applyIntent),
          }))
          .then(async (response) => {
            if (!response.ok) throw new Error(`apply handoff failed (${response.status})`);
            return response.json() as Promise<{ redirect_url: string }>;
          })
          .then(({ redirect_url }) => {
            sessionStorage.removeItem(APPLY_INTENT_KEY);
            window.location.replace(redirect_url);
          })
          .catch((error) => {
            setApplyError(error instanceof Error ? error.message : 'apply handoff failed');
            setStep('idle');
            handoffStarted.current = false;
          });
        return;
      }
      window.location.replace(resolvePortalUrl());
    }
  }, [principal, loading, resolvePortalUrl, getToken]);

  const handleLogin = () => {
    setStep('connecting');
    const applyIntent = applyIntentFromLocation();
    persistApplyIntent(applyIntent);
    login(applyIntent ? { appState: { returnTo: window.location.href, applyIntent } } : undefined);
  };

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden flex flex-col items-center justify-center">
      <DotGrid />
      <GlowOrb />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 flex flex-col items-center gap-10 px-6 py-12 w-full max-w-md"
      >
        {/* LOGO */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="flex flex-col items-center gap-3"
        >
          <img src={logoHeader} alt="XYQAR" className="h-12 w-auto" />
          <div className="text-center">
            <p className="text-[22px] font-bold tracking-[0.25em] text-white">XYQAR</p>
            <p className="text-[9px] tracking-[0.15em] text-white/40 uppercase mt-1">
              Powered by QYQA Labs
            </p>
          </div>
        </motion.div>

        {/* HEADLINE */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-center"
        >
          <h1
            className="text-3xl sm:text-4xl font-normal text-white mb-3 leading-tight"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Your access.<br />Your identity.
          </h1>
          <p className="text-sm text-white/40 tracking-wide">
            Client, talent, partner — one secure gateway.
          </p>
        </motion.div>

        {/* AUTH */}
        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center gap-4"
            >
              <motion.div
                className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full"
                animate={{ rotate: 360 }}
                transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }}
              />
              <p className="text-xs text-white/40 tracking-widest uppercase">Verifying session…</p>
            </motion.div>
          ) : principal && applyError ? (
            <motion.div
              key="apply-error"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center gap-4 text-center"
            >
              <p className="text-xs text-red-200 tracking-widest uppercase">Apply handoff failed</p>
              <p className="text-xs text-white/35">{applyError}</p>
            </motion.div>
          ) : principal ? (
            <motion.div
              key="redirecting"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center gap-4"
            >
              <motion.div
                className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full"
                animate={{ rotate: 360 }}
                transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }}
              />
              <p className="text-xs text-white/40 tracking-widest uppercase">Redirecting…</p>
            </motion.div>
          ) : (
            <motion.div
              key="auth"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
              className="w-full flex flex-col gap-4"
            >
              {/* Single enterprise CTA — Auth0 Universal Login handles all connections */}
              <motion.button
                onClick={handleLogin}
                disabled={step === 'connecting'}
                whileHover={{ scale: 1.02, boxShadow: '0 0 24px rgba(255,255,255,0.15)' }}
                whileTap={{ scale: 0.98 }}
                className="w-full flex items-center justify-center gap-3 bg-white text-black py-4 px-6 rounded-full text-sm font-bold tracking-[0.15em] uppercase transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {step === 'connecting' ? (
                  <>
                    <motion.div
                      className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }}
                    />
                    Connecting…
                  </>
                ) : (
                  <>
                    <Lock className="h-4 w-4" />
                    Sign in securely
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </motion.button>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="text-center text-[10px] text-white/25 tracking-wide leading-relaxed"
              >
                {applyError && (
                  <>
                    Apply handoff failed. Restart from the Apply link.
                    <br />
                  </>
                )}
                Professional email required. MFA enforced.
                <br />
                Sessions are monitored and cryptographically logged.
              </motion.p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* SECURITY BADGES */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="flex items-center gap-6 text-white/20"
        >
          <div className="flex items-center gap-1.5">
            <Shield className="h-3 w-3" />
            <span className="text-[9px] tracking-widest uppercase">Xyqar Identity RS256</span>
          </div>
          <div className="w-px h-3 bg-white/10" />
          <div className="flex items-center gap-1.5">
            <Lock className="h-3 w-3" />
            <span className="text-[9px] tracking-widest uppercase">E2E Encrypted</span>
          </div>
          <div className="w-px h-3 bg-white/10" />
          <span className="text-[9px] tracking-widest uppercase">GDPR</span>
        </motion.div>
      </motion.div>

      <GlobalFooter />
    </div>
  );
}
